import React, { useEffect, useMemo, useState } from 'react';
import escortIcon from '../../assets/icons/ic_escort.svg'
import IconTint from 'react-icon-tint';
import Dropdown from '../Dropdown';
import LocationSelectionGrid from '../LocationSelectionGrid';
import ItemComboBox from '../ItemComboBox';
import { useAtom } from 'jotai';
import { fetchFrom, fetchTo, fetchItem } from '../store/DataStore';
import ModalShell from './ModalShell';
import VoiceTextButton from './VoiceTextButton';
import { HOME_BASE_LOCATION_NAMES, getSortedVisibleLocations, getVisibleLocationOrDefault } from './locationVisibility';

const LOCATION_GRID_THRESHOLD = 30;

const FetchPopup = ({ isOpen, children, onClose, onSend, locations, currentLocation, areaSelected, defaultItems = []}) => {
    const [show, setShow] = useState(isOpen);
    const [isDropdownTouched, setIsDropdownTouched] = useState(false)
    const [selectedFromLocation, setSelectedFromLocation] = useAtom(fetchFrom);
    const [selectedToLocation, setSelectedToLocation] = useAtom(fetchTo);
    const [itemFetched, setItemFetched] = useAtom(fetchItem);
    const sortedLocations = useMemo(() => getSortedVisibleLocations(locations, HOME_BASE_LOCATION_NAMES), [locations]);

    const useLocationGrid = sortedLocations.length < LOCATION_GRID_THRESHOLD;

    useEffect(() => {
        if (isOpen) {
            setShow(true);
            if (!isDropdownTouched) {
                setSelectedToLocation(getVisibleLocationOrDefault(currentLocation, sortedLocations));
                setSelectedFromLocation(getVisibleLocationOrDefault(areaSelected, sortedLocations));
            }
        } else {
            setShow(false);
            setTimeout(onClose, 200);
        }
    }, [isOpen, onClose, isDropdownTouched, areaSelected, currentLocation, sortedLocations, setSelectedToLocation, setSelectedFromLocation]);

    const handleClose = () => {
        setShow(false);
        setTimeout(onClose, 200);
        setItemFetched('');
        setSelectedFromLocation(selectedFromLocation);
        setSelectedToLocation(selectedToLocation);
        setIsDropdownTouched(false);
    };

    const handleSend = () => {
        onSend();
        setShow(false);
        setTimeout(onClose, 200);
        setItemFetched('');
        setSelectedFromLocation(selectedFromLocation);
        setSelectedToLocation(selectedToLocation);
        setIsDropdownTouched(false);
    };

    const setSelectedFrom = (item) => {
        setIsDropdownTouched(true);
        setSelectedFromLocation(item);
    }

    const setSelectedTo = (item) => {
        setIsDropdownTouched(true);
        setSelectedToLocation(item);
    }

    const canSend = selectedFromLocation !== selectedToLocation &&
        itemFetched !== '' &&
        sortedLocations.includes(selectedFromLocation) &&
        sortedLocations.includes(selectedToLocation);

    const onItemChanged = (item) => {
        setIsDropdownTouched(true);
        setItemFetched(item);
    }

    const onVoiceItemChanged = (item) => {
        setItemFetched(item);
    }

    return (
        <ModalShell isMounted={show || isOpen} isOpen={show} onClose={handleClose} maxWidth="md">
            <div className="-mt-2 flex flex-col items-center text-center">
                {children}
            </div>
            <div className="mt-5 flex flex-col gap-4">
                <div>
                    <label htmlFor="fetch-item-description" className="block font-heebo text-md font-light">Item</label>
                    <div className="relative">
                        <ItemComboBox
                            id="fetch-item-description"
                            className="h-11 w-full rounded-md bg-[#3C5BD2] bg-opacity-10 px-3 pr-16 outline-none font-heebo font-light"
                            placeholder="Item description"
                            value={itemFetched}
                            onChange={onItemChanged}
                            items={defaultItems}
                        >
                            <VoiceTextButton
                                value={itemFetched}
                                onChange={onVoiceItemChanged}
                                ariaLabel="Dictate item description"
                                isActive={show && isOpen}
                                positionClassName={defaultItems.length ? 'right-10 top-1/2 -translate-y-1/2' : 'right-2 top-1/2 -translate-y-1/2'}
                            />
                        </ItemComboBox>
                    </div>
                </div>
                {useLocationGrid ? (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                            <span className="mb-2 block font-heebo text-md font-light">From</span>
                            <LocationSelectionGrid
                                locations={sortedLocations}
                                selectedLocation={selectedFromLocation}
                                onSelect={setSelectedFrom}
                                columnsClass="grid-cols-1 md:grid-cols-2"
                                buttonClassName="w-full min-w-0"
                                emptyMessage="No locations available"
                            />
                        </div>
                        <div>
                            <span className="mb-2 block font-heebo text-md font-light">To</span>
                            <LocationSelectionGrid
                                locations={sortedLocations}
                                selectedLocation={selectedToLocation}
                                onSelect={setSelectedTo}
                                columnsClass="grid-cols-1 md:grid-cols-2"
                                buttonClassName="w-full min-w-0"
                                emptyMessage="No locations available"
                            />
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <label className="block">
                            <span className="font-heebo text-md font-light">From</span>
                            <Dropdown
                                list={sortedLocations}
                                setSelected={(item) => setSelectedFrom(item)}
                                selectedItem={selectedFromLocation}
                                fullWidth={true}
                            />
                        </label>
                        <label className="block">
                            <span className="font-heebo text-md font-light">To</span>
                            <Dropdown
                                list={sortedLocations}
                                setSelected={(item) => setSelectedTo(item)}
                                selectedItem={selectedToLocation}
                                fullWidth={true}
                            />
                        </label>
                    </div>
                )}
            </div>
            <div className="mt-8 flex justify-center">
                {canSend ? (
                    <button
                        type="button"
                        className="flex w-full items-center justify-center rounded-3xl bg-[#152FF5] px-5 py-2 text-white sm:w-auto"
                        onClick={handleSend}
                    >
                        <span className="font-heebo">Confirm Fetch</span>
                        <IconTint src={escortIcon} className='ml-2 h-5' color='#FFFFFF' />
                    </button>
                ) : (
                    <div className="flex w-full items-center justify-center rounded-3xl bg-[#152FF5] px-5 py-2 opacity-50 sm:w-auto">
                        <span className="font-heebo text-white">Confirm Fetch</span>
                        <IconTint src={escortIcon} className='ml-2 h-5' color='#FFFFFF' />
                    </div>
                )}
            </div>
        </ModalShell>
    );
};

export default FetchPopup;
